import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import type { PerformanceTimePeriodName } from '@savvy/constants/generated/gql-enums';
import { PERFORMANCE_TIME_PERIOD_ALL_TIME } from '@savvy/constants/performance-time-periods';

import type { ClientPortalHomePerformancePeriod } from '../../api/home-performance-query';
import type { ClientPortalHomeTask } from '../../api/home-tasks-query';
import type { ClientPortalTimeRange } from '../common/constants';
import type {
  HomeAccountGroup,
  HomeBalancesFreshness,
  HomeDayChange,
  HomeGraphValue,
  HomeHistoricalGroup,
  HomeInvestmentRow,
  HomeSeriesPoint,
  HomeTaskDueState,
  HomeTaskRow,
} from './types';
import { accountDetailHref, accountDisplayName } from '../accounts/utils';
import { clientTaskHref, openClientTasks } from '../common/tasks';
import { timeRangeAnchor, timeRangeWindowStart } from '../common/time-ranges';
import { toFiniteNumber } from '../common/utils';
import {
  DAY_CHANGE_MAX_AGE_DAYS,
  DAY_CHANGE_MAX_PERCENT,
  HOME_DAY_CHANGE_LABEL,
  HOME_DAY_CHANGE_NO_READING_NOTE,
  HOME_DAY_CHANGE_REFRESH_NOTE,
  HOME_HELD_AWAY_GROUP,
  HOME_INVESTMENT_GROUPS,
  HOME_MANAGED_GROUP,
  HOME_TASK_NO_DUE_DATE_LABEL,
  HOME_TASK_OVERDUE_LABEL,
  homeDayChangeUpdatedLabel,
  homeDayChangeWindowNote,
} from './constants';

const INVESTMENT_GROUP_NAMES: readonly string[] = HOME_INVESTMENT_GROUPS;

export function findPeriod(
  periods: readonly ClientPortalHomePerformancePeriod[],
  period: PerformanceTimePeriodName,
): ClientPortalHomePerformancePeriod | undefined {
  return periods.find((entry) => entry.timePeriod === period);
}

function valuesByDate(group: HomeHistoricalGroup | undefined): Map<string, number> {
  const totals = new Map<string, number>();
  if (isNil(group)) {
    return totals;
  }
  for (const historical of group.historicalValues) {
    const amount = toFiniteNumber(historical.value.amount);
    if (!isNil(amount)) {
      totals.set(historical.date, amount);
    }
  }
  return totals;
}

function findGroup(groups: readonly HomeHistoricalGroup[], name: string): HomeHistoricalGroup | undefined {
  return groups.find((group) => group.name === name);
}

function withoutReadingsTakenBeforeFunding(points: readonly HomeSeriesPoint[]): HomeSeriesPoint[] {
  const firstFunded = points.findIndex((point) => point.total !== 0);
  return firstFunded === -1 ? [] : points.slice(firstFunded);
}

export function buildInvestmentsSeries(
  groups: readonly HomeHistoricalGroup[],
  period: ClientPortalTimeRange,
): HomeSeriesPoint[] {
  const managedByDate = valuesByDate(findGroup(groups, HOME_MANAGED_GROUP));
  const heldAwayByDate = valuesByDate(findGroup(groups, HOME_HELD_AWAY_GROUP));
  if (managedByDate.size === 0 && heldAwayByDate.size === 0) {
    return [];
  }

  const dates = [...new Set([...managedByDate.keys(), ...heldAwayByDate.keys()])].sort((left, right) =>
    left.localeCompare(right),
  );
  const windowStart = timeRangeWindowStart(period, timeRangeAnchor(dates.at(-1)))?.toISODate() ?? null;

  let carriedManaged: number | null = null;
  let carriedHeldAway: number | null = null;
  const points: HomeSeriesPoint[] = [];

  for (const date of dates) {
    carriedManaged = managedByDate.get(date) ?? carriedManaged;
    carriedHeldAway = heldAwayByDate.get(date) ?? carriedHeldAway;
    if (!isNil(windowStart) && date < windowStart) {
      continue;
    }
    points.push({
      date,
      total: (carriedManaged ?? 0) + (carriedHeldAway ?? 0),
      managed: managedByDate.get(date) ?? null,
      heldAway: heldAwayByDate.get(date) ?? null,
    });
  }

  return withoutReadingsTakenBeforeFunding(points);
}

export function graphValuesInWindow(
  graphValues: readonly HomeGraphValue[] | undefined,
  period: ClientPortalTimeRange,
  windowStart: string | null,
): readonly HomeGraphValue[] {
  const wholeHistory = period === PERFORMANCE_TIME_PERIOD_ALL_TIME;
  const points = [...(graphValues ?? [])]
    .filter((point) => !wholeHistory || point.includeInAllTime)
    .sort((left, right) => left.date.localeCompare(right.date));
  if (isNil(windowStart)) {
    return points;
  }
  const openingReading = points.findLastIndex((point) => point.date <= windowStart);
  return openingReading === -1 ? points.filter((point) => point.date >= windowStart) : points.slice(openingReading);
}

function readingsCarriedOnto(byDate: Map<string, number>, dates: readonly string[]): readonly (number | null)[] {
  const readings = [...byDate.entries()].sort(([left], [right]) => left.localeCompare(right));
  let cursor = 0;
  let carried: number | null = null;
  return dates.map((date) => {
    while (cursor < readings.length && (readings[cursor]?.[0] ?? '') <= date) {
      carried = readings[cursor]?.[1] ?? carried;
      cursor += 1;
    }
    return carried;
  });
}

export function buildInvestmentsValueSeries(
  managedValues: readonly HomeGraphValue[],
  groups: readonly HomeHistoricalGroup[],
  windowStart: string | null,
): HomeSeriesPoint[] {
  const heldAwayByDate = valuesByDate(findGroup(groups, HOME_HELD_AWAY_GROUP));
  const managedByDate = new Map<string, number>();
  for (const point of managedValues) {
    const amount = toFiniteNumber(point.valueUsd);
    if (!isNil(amount)) {
      managedByDate.set(point.date, amount);
    }
  }

  const dates = (
    managedByDate.size > 0
      ? [...managedByDate.keys()]
      : [...heldAwayByDate.keys()].filter((date) => isNil(windowStart) || date >= windowStart)
  ).sort((left, right) => left.localeCompare(right));
  const heldAway = readingsCarriedOnto(heldAwayByDate, dates);

  return withoutReadingsTakenBeforeFunding(
    dates.map((date, index) => {
      const managed = managedByDate.get(date) ?? null;
      const heldAwayReading = heldAway[index] ?? null;
      return { date, total: (managed ?? 0) + (heldAwayReading ?? 0), managed, heldAway: heldAwayReading };
    }),
  );
}

type LegMove = { readonly open: number; readonly change: number };

const INVESTMENT_LEGS = ['managed', 'heldAway'] as const;

type InvestmentLeg = (typeof INVESTMENT_LEGS)[number];

function legReadingOnOrBefore(series: readonly HomeSeriesPoint[], leg: InvestmentLeg, date: string): number | null {
  let latest: number | null = null;
  for (const point of series) {
    if (point.date > date) {
      break;
    }
    latest = point[leg] ?? latest;
  }
  return latest;
}

export function seriesAsOfDate(series: readonly HomeSeriesPoint[]): string | null {
  return series.at(-1)?.date ?? null;
}

const SATURDAY = 6;

function isWeekend(day: DateTime): boolean {
  return day.weekday >= SATURDAY;
}

function isRecentReading(isoDate: string): boolean {
  const reading = DateTime.fromISO(isoDate).startOf('day');
  const today = DateTime.now().startOf('day');
  if (!reading.isValid || reading >= today) {
    return reading.isValid;
  }

  let businessDaysElapsed = 0;
  for (let day = reading.plus({ days: 1 }); day <= today; day = day.plus({ days: 1 })) {
    if (isWeekend(day)) {
      continue;
    }
    businessDaysElapsed += 1;
    if (businessDaysElapsed > DAY_CHANGE_MAX_AGE_DAYS) {
      return false;
    }
  }
  return true;
}

export function balancesFreshness(series: readonly HomeSeriesPoint[]): HomeBalancesFreshness {
  const asOfDate = seriesAsOfDate(series);
  if (isNil(asOfDate)) {
    return { kind: 'unavailable' };
  }
  return isRecentReading(asOfDate) ? { kind: 'current', asOfDate } : { kind: 'stale', asOfDate };
}

function genuineReadings(series: readonly HomeSeriesPoint[]): readonly HomeSeriesPoint[] {
  const readings: HomeSeriesPoint[] = [];
  for (const point of series) {
    const previous = readings.at(-1);
    if (isNil(previous) || previous.total !== point.total) {
      readings.push(point);
    }
  }
  return readings;
}

export function latestDayChange(series: readonly HomeSeriesPoint[]): HomeDayChange {
  const readings = genuineReadings(series);
  const last = readings.at(-1);
  const previous = readings.at(-2);
  if (isNil(last) || isNil(previous)) {
    return { kind: 'unavailable', reason: 'no_history', latestDate: last?.date ?? null };
  }

  if (!isRecentReading(last.date)) {
    return { kind: 'unavailable', reason: 'stale_balances', latestDate: last.date };
  }

  const moves = INVESTMENT_LEGS.map((leg) => {
    const open = legReadingOnOrBefore(series, leg, previous.date);
    const close = legReadingOnOrBefore(series, leg, last.date);
    return isNil(open) || isNil(close) ? null : { open, change: close - open };
  }).filter((move): move is LegMove => !isNil(move));

  const openingValue = moves.reduce((total, move) => total + move.open, 0);
  if (moves.length === 0 || openingValue === 0) {
    return { kind: 'unavailable', reason: 'basis_change', latestDate: last.date };
  }

  const amount = moves.reduce((total, move) => total + move.change, 0);
  const percentMove = (amount / openingValue) * 100;
  const isBasisChangeRatherThanMarketMove = Math.abs(percentMove) > DAY_CHANGE_MAX_PERCENT;
  if (!Number.isFinite(percentMove) || isBasisChangeRatherThanMarketMove) {
    return { kind: 'unavailable', reason: 'basis_change', latestDate: last.date };
  }

  return {
    kind: 'change',
    amount,
    percent: percentMove,
    previousDate: previous.date,
    latestDate: last.date,
  };
}

function readingDateLabel(isoDate: string): string {
  const parsed = DateTime.fromISO(isoDate);
  if (!parsed.isValid) {
    return isoDate;
  }
  return parsed.toFormat(parsed.year === DateTime.now().year ? 'LLL d' : 'LLL d, yyyy');
}

export function dayChangeUpdatedLabel(change: HomeDayChange): string {
  return isNil(change.latestDate)
    ? HOME_DAY_CHANGE_LABEL
    : homeDayChangeUpdatedLabel(readingDateLabel(change.latestDate));
}

export function dayChangeTooltip(change: HomeDayChange): string {
  if (change.kind === 'change') {
    return `${homeDayChangeWindowNote(readingDateLabel(change.previousDate), readingDateLabel(change.latestDate))} ${HOME_DAY_CHANGE_REFRESH_NOTE}`;
  }
  return isNil(change.latestDate) ? HOME_DAY_CHANGE_NO_READING_NOTE : HOME_DAY_CHANGE_REFRESH_NOTE;
}

export function twrToPercentPoints(twr: string | null | undefined): number | null {
  const parsed = toFiniteNumber(twr);
  return isNil(parsed) ? null : parsed * 100;
}

function isVisibleToClientAndCountedInTotals(account: HomeAccountGroup['accounts'][number]): boolean {
  return account.hiddenFromClient !== true && account.hiddenFromNetWorth !== true;
}

export function investmentRows(groups: readonly HomeAccountGroup[] | undefined): readonly HomeInvestmentRow[] {
  if (isNil(groups)) {
    return [];
  }
  return groups
    .filter((group) => INVESTMENT_GROUP_NAMES.includes(group.name))
    .flatMap((group) =>
      group.accounts.filter(isVisibleToClientAndCountedInTotals).map((account) => ({
        id: account.id,
        name: accountDisplayName(account),
        href: accountDetailHref(account),
        balance: toFiniteNumber(account.displayBalance.amount),
        isManaged: group.name === HOME_MANAGED_GROUP,
        providerName: account.providerAccount?.providerName ?? null,
        providerLogoUrl: account.providerLogoUrl,
      })),
    )
    .sort((left, right) => (right.balance ?? 0) - (left.balance ?? 0));
}

export function investmentsTotal(rows: readonly HomeInvestmentRow[]): number | null {
  if (rows.length === 0 || rows.some((row) => isNil(row.balance))) {
    return null;
  }
  return rows.reduce((total, row) => total + (row.balance ?? 0), 0);
}

export function formatAsOfDate(isoDate: string | null | undefined): string | null {
  if (isNil(isoDate)) {
    return null;
  }
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : null;
}

const NO_DUE_DATE: HomeTaskDueState = { label: HOME_TASK_NO_DUE_DATE_LABEL, overdue: false };

export function taskDueState(dueAt: string | null | undefined): HomeTaskDueState {
  if (isNil(dueAt)) {
    return NO_DUE_DATE;
  }
  const due = DateTime.fromISO(dueAt);
  if (!due.isValid) {
    return NO_DUE_DATE;
  }
  const overdue = due.startOf('day') < DateTime.now().startOf('day');
  return { label: overdue ? HOME_TASK_OVERDUE_LABEL : `Due ${due.toFormat('LLL d')}`, overdue };
}

export function homeTaskRows(
  tasks: readonly ClientPortalHomeTask[] | undefined,
  ownOnboardingTaskIds?: ReadonlySet<string>,
): readonly HomeTaskRow[] {
  return openClientTasks(tasks, ownOnboardingTaskIds).map((task) => ({
    id: task.id,
    name: task.name,
    href: clientTaskHref(task),
    due: taskDueState(task.dueAt),
  }));
}
